import { Box, Stack, StackProps, SxProps, Theme, alpha, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { SystemStyleObject } from '@mui/system';
import React, { useMemo, useRef } from 'react';

import { useOverflowState } from '../Hooks/useOverflowState';

type OverflowValue = 'auto' | 'hidden' | 'scroll' | 'visible';

// =========================================================
// Scroll Overflow Chevron Indicator
// =========================================================

// Pixel distance the chevron travels in its bounce animation. Also used as
// the inward offset from the container edge so the icon never gets clipped
// by the wrapper's `overflow: hidden` at maximum bounce displacement.
const BOUNCE_DISTANCE = 3;

interface ScrollIndicatorProps {
  position: 'start' | 'end';
  direction: StackProps['direction'];
  /** Whether this indicator should be visible (fades in) or hidden (fades out). */
  visible: boolean;
  /** Whether the chevron plays its bounce animation. Defaults to false. */
  animated?: boolean;
}

/**
 * A chevron icon anchored at the bottom corner of a scrollable container
 * to indicate that more content is available in that direction.
 *
 * For row-direction stacks the icon sits at the bottom-left (start) or
 * bottom-right (end). For column-direction stacks it is horizontally
 * centered at the top (start) or bottom (end) edge.
 *
 * The icon is offset inward from the edge by `BOUNCE_DISTANCE` pixels so
 * that the bounce animation never pushes it outside the parent's clipping
 * boundary. A semi-transparent white circle behind the icon provides
 * contrast against varying content backgrounds.
 *
 * Visibility is controlled by opacity with a 250ms fade transition.
 * The `visible` prop is driven by overflow state and hover detection in
 * the parent ScrollableStack — indicators fade out while hovered (when
 * the scrollbar itself becomes visible).
 */
function ScrollIndicator({ position, direction, visible, animated = false }: ScrollIndicatorProps): React.JSX.Element {
  const isColumn = direction === 'column' || direction === 'column-reverse';

  const Icon = isColumn
    ? position === 'start'
      ? KeyboardArrowUpIcon
      : KeyboardArrowDownIcon
    : position === 'start'
      ? ChevronLeftIcon
      : ChevronRightIcon;

  const bounceKeyframe = isColumn
    ? position === 'start'
      ? 'indicatorBounceUp'
      : 'indicatorBounceDown'
    : position === 'start'
      ? 'indicatorBounceLeft'
      : 'indicatorBounceRight';

  // Row stacks: bottom corner, inset by BOUNCE_DISTANCE from the edge.
  // Column stacks: horizontally centered, at the top or bottom edge.
  const positionStyles: SystemStyleObject<Theme> = isColumn
    ? {
        left: '50%',
        transform: 'translateX(-50%)',
        ...(position === 'start' ? { top: BOUNCE_DISTANCE } : { bottom: BOUNCE_DISTANCE }),
      }
    : {
        bottom: 0,
        ...(position === 'start' ? { left: BOUNCE_DISTANCE } : { right: BOUNCE_DISTANCE }),
      };

  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 1,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: 'opacity 250ms ease',
        // Each keyframe moves the icon BOUNCE_DISTANCE pixels toward the
        // edge it points at and back, creating a gentle pulsing motion.
        '@keyframes indicatorBounceRight': {
          '0%, 100%': { transform: 'translateX(0)' },
          '50%': { transform: `translateX(${BOUNCE_DISTANCE}px)` },
        },
        '@keyframes indicatorBounceLeft': {
          '0%, 100%': { transform: 'translateX(0)' },
          '50%': { transform: `translateX(-${BOUNCE_DISTANCE}px)` },
        },
        '@keyframes indicatorBounceDown': {
          '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
          '50%': { transform: `translateX(-50%) translateY(${BOUNCE_DISTANCE}px)` },
        },
        '@keyframes indicatorBounceUp': {
          '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
          '50%': { transform: `translateX(-50%) translateY(-${BOUNCE_DISTANCE}px)` },
        },
        ...positionStyles,
      }}
    >
      <Icon
        sx={(theme) => ({
          display: 'block',
          color: theme.palette.text.secondary,
          fontSize: 20,
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          borderRadius: '50%',
          animation: animated ? `${bounceKeyframe} 1.2s ease-in-out infinite` : 'none',
        })}
      />
    </Box>
  );
}

// =========================================================
// Scrollbar Style Helpers
// =========================================================

/**
 * Determines the overflow defaults based on the stack direction. The primary
 * axis gets 'auto' (scrollable when content overflows), the cross axis is
 * hidden. Explicit overrides take precedence.
 */
function resolveOverflow(
  direction: StackProps['direction'],
  oxOverride?: OverflowValue,
  oyOverride?: OverflowValue,
): { overflowX: OverflowValue; overflowY: OverflowValue } {
  const isColumn = direction === 'column' || direction === 'column-reverse';
  return {
    overflowX: oxOverride ?? (isColumn ? 'hidden' : 'auto'),
    overflowY: oyOverride ?? (isColumn ? 'auto' : 'hidden'),
  };
}

/**
 * Combines all scroll-related styles into a single sx-compatible object.
 * Returns an empty object when no size constraint is set, since overflow
 * cannot occur without one and scroll styles would be unnecessary.
 */
function buildScrollSx(
  isConstrained: boolean,
  direction: StackProps['direction'],
  sbWidth: string,
  sbColor: string,
  showOnlyOnHover: boolean,
  oxOverride?: OverflowValue,
  oyOverride?: OverflowValue,
): SystemStyleObject<Theme> {
  if (!isConstrained) {
    return {};
  }

  const overflow = resolveOverflow(direction, oxOverride, oyOverride);

  if (showOnlyOnHover) {
    // Always reserve scrollbar space (scrollbarWidth: thin) to prevent
    // layout shifts on hover. Only the color toggles between transparent
    // (hidden) and visible.
    return {
      ...overflow,
      scrollbarWidth: sbWidth,
      scrollbarColor: 'transparent transparent',
      '&:hover': { scrollbarColor: sbColor },
    };
  }

  return {
    ...overflow,
    scrollbarColor: sbColor,
    scrollbarWidth: sbWidth,
  };
}

/**
 * Merges the scroll styles with any caller-provided sx, preserving array
 * form if the caller passed an sx array.
 */
// The cast in the object branch is safe: SxProps<Theme> is a union of
// SystemStyleObject<Theme>, a callback, or an array of those. After
// ruling out arrays and non-objects, only SystemStyleObject remains.
// TypeScript cannot narrow the union sufficiently on its own.
function mergeSx(
  scrollSx: SystemStyleObject<Theme>,
  sxProp?: SxProps<Theme>,
): SystemStyleObject<Theme> | SxProps<Theme> {
  if (Array.isArray(sxProp)) {
    return [scrollSx, ...sxProp];
  }
  if (sxProp && typeof sxProp === 'object') {
    return { ...scrollSx, ...(sxProp as SystemStyleObject<Theme>) };
  }
  return scrollSx;
}

// =========================================================
// ScrollableStack Component
// =========================================================

interface ScrollableStackProps extends StackProps {
  /** Scrollbar thickness. Defaults to 'thin'. */
  scrollbarWidth?: 'auto' | 'thin' | 'none';
  /**
   * Scrollbar thumb and track color as a CSS `scrollbar-color` value.
   * Defaults to a subtle dark thumb on a transparent track.
   */
  scrollbarColor?: string;
  /** Override horizontal scrollbar behavior. Defaults to 'auto' for direction='row', 'hidden' otherwise. */
  overflowX?: OverflowValue;
  /** Override vertical scrollbar behavior. Defaults to 'auto' for direction='column', 'hidden' otherwise. */
  overflowY?: OverflowValue;
  /** Show scrollbar only when hovering the container. Defaults to true. */
  showOnlyOnHover?: boolean;
  /** Show chevron indicators at edges when content overflows. Defaults to true when constrained. */
  showOverflowIndicators?: boolean;
  /** Animate the overflow indicators with a bounce effect. Defaults to false. */
  animateIndicators?: boolean;
}

/**
 * A Stack with built-in scrollbar management. Scrollbar visibility is
 * derived from the stack direction: the primary axis scrolls when content
 * overflows, the cross axis is hidden. Both axes can be overridden
 * explicitly. Scrollbar appearance defaults to thin and unobtrusive.
 *
 * When content overflows, small bouncing chevron icons appear at the
 * bottom corners to hint that more content is available. These indicators
 * fade out while the mouse hovers (when the scrollbar becomes visible
 * instead).
 *
 * Scroll styles are only applied when a size constraint is set (maxWidth,
 * maxHeight, width, or height), since without one overflow cannot occur.
 * A ScrollableStack with `width="100%"` inside a proportionally-sized
 * table cell is parent-constrained and will enable scrolling.
 */
export default function ScrollableStack({
  scrollbarWidth: sbWidth = 'thin',
  scrollbarColor,
  overflowX: oxOverride,
  overflowY: oyOverride,
  showOnlyOnHover = true,
  showOverflowIndicators,
  animateIndicators = false,
  direction,
  sx: sxProp,
  ...rest
}: ScrollableStackProps): React.JSX.Element {
  const theme = useTheme();
  // Default thumb is 25%-alpha text.primary so it auto-flips in dark mode;
  // track stays transparent so it blends with whatever surface it sits on.
  const sbColor = scrollbarColor ?? `${alpha(theme.palette.text.primary, 0.25)} transparent`;
  const stackRef = useRef<HTMLDivElement | null>(null);
  // A stack is constrained when it has an explicit size limit — either its
  // own (maxWidth/maxHeight) or one inherited from a parent (width/height).
  // A ScrollableStack with width="100%" inside a proportionally-sized table
  // cell is parent-constrained and must enable scroll styles.
  const isConstrained =
    rest.maxWidth !== undefined ||
    rest.maxHeight !== undefined ||
    rest.width !== undefined ||
    rest.height !== undefined;

  // Default to showing indicators when the stack is constrained
  const indicatorsEnabled = showOverflowIndicators ?? isConstrained;

  const scrollSx = useMemo(
    () => buildScrollSx(isConstrained, direction, sbWidth, sbColor, showOnlyOnHover, oxOverride, oyOverride),
    [isConstrained, direction, sbWidth, sbColor, showOnlyOnHover, oxOverride, oyOverride],
  );

  const mergedSx = useMemo(() => mergeSx(scrollSx, sxProp), [scrollSx, sxProp]);

  const { canScrollStart, canScrollEnd, isHovered } = useOverflowState(stackRef, direction, indicatorsEnabled);

  const showStart = indicatorsEnabled && canScrollStart && !isHovered;
  const showEnd = indicatorsEnabled && canScrollEnd && !isHovered;

  if (!indicatorsEnabled) {
    // No wrapper needed — keep the simple path
    return <Stack direction={direction} sx={mergedSx} {...rest} />;
  }

  // Size constraints (width, maxWidth, height, maxHeight) must live on the
  // outer wrapper Box — not the inner Stack — so the wrapper itself is
  // constrained. The inner Stack then fills the wrapper via flex and scrolls
  // when content overflows. Without this, the inline-flex wrapper would grow
  // to content size, making overflow impossible.
  const { width, maxWidth, height, maxHeight, ...stackRest } = rest;
  return (
    <Box
      position="relative"
      display="inline-flex"
      overflow="hidden"
      width={width}
      maxWidth={maxWidth}
      height={height}
      maxHeight={maxHeight}
    >
      <Stack ref={stackRef} direction={direction} sx={mergedSx} {...stackRest} />
      <ScrollIndicator position="start" direction={direction} visible={showStart} animated={animateIndicators} />
      <ScrollIndicator position="end" direction={direction} visible={showEnd} animated={animateIndicators} />
    </Box>
  );
}
